import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
  where,
  deleteDoc,
  getDocs,
} from 'firebase/firestore';
import { StateCreator } from 'zustand';
import { firestore } from '../../settings/firebase';
import { LaunchStatus } from './store';
import { v4 } from 'uuid';

export interface IndexDistributionFlowSlice {
  value: LaunchStatus[];
  hash: string;
  get: (name: string) => Promise<void>;
  adminList: (force?: boolean) => Promise<void>;
  list: (username: string, force?: boolean) => Promise<void>;
  subscribe: (namespaces: string[]) => {
    unSubscribe: () => void;
  };
  subscribeAsAdmin: () => {
    unSubscribe: () => void;
  };
  save: (item: SaveLaunchStatus) => Promise<{ ok: boolean } | { error: string }>;
  delete: (name: string) => Promise<{ ok: boolean }>;
}

type SaveLaunchStatus = Omit<LaunchStatus, 'modified'>;

export const createIndexDistributionFlowSlice: StateCreator<
  IndexDistributionFlowSlice,
  [],
  [],
  IndexDistributionFlowSlice
> = (setState, getState) => ({
  value: [],
  hash: '',
  // Get the distribution flow item by indexName
  get: async name => {
    const requestRef = doc(firestore, 'indexDistributionFlow', name);
    const requestDoc = (await getDoc(requestRef)).data() as LaunchStatus;

    requestDoc && setState({ value: [requestDoc] });
  },

  adminList: async (force = false) => {
    if (!force && getState().value.length) return;

    const querySnapshot = await getDocs(collection(firestore, 'indexDistributionFlow'));

    const value = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...(doc.data() as LaunchStatus),
    }));

    setState({ value, hash: v4() });
  },

  list: async (username: string, force = false) => {
    if (!force && getState().value.length) return;

    const requestsRef = collection(firestore, 'indexDistributionFlow');
    const queryRequestSnapshot = query(requestsRef, where('requestedBy', '==', username));
    // TODO: Uncomment if external approval is implemented
    // const queryApproveSnapshot = query(requestsRef, where('approvedBy', '==', username));

    const requestedBySnapshot = await getDocs(queryRequestSnapshot);
    // const approvedBySnapshot = await getDocs(queryApproveSnapshot);

    const value = requestedBySnapshot.docs.map(doc => ({
      id: doc.id,
      ...(doc.data() as LaunchStatus),
    }));

    setState({ value, hash: v4() });
  },

  // Subscribe to changes in the distribution flow (this will update the UI in real time)
  subscribeAsAdmin: () => {
    const requestsRef = collection(firestore, 'indexDistributionFlow');

    const unSubscribe = onSnapshot(requestsRef, snapshot => {
      // const value = getState().value;
      const value: LaunchStatus[] = [];
      snapshot.forEach(doc => {
        const data = doc.data() as LaunchStatus;
        value.push(data);
      });
      setState({ value, hash: v4() });
    });

    // To stop listening to changes when the component unmounts
    return {
      unSubscribe,
    };
  },
  // Subscribe to changes in the distribution flow (this will update the UI in real time)
  subscribe: (namespaces: string[]) => {
    const requestsRef = collection(firestore, 'indexDistributionFlow');
    const namespaceChunks = chunkArray(namespaces, MAX_IN_CLAUSE);

    const allUnsubscribers: (() => void)[] = [];

    const combinedDataMap = new Map<string, LaunchStatus>();

    namespaceChunks.forEach(chunk => {
      const q = query(requestsRef, where('namespace', 'in', chunk));
      const unsubscribe = onSnapshot(q, snapshot => {
        snapshot.forEach(doc => {
          const data = doc.data() as LaunchStatus;
          combinedDataMap.set(doc.id, data);
        });

        // Recalculate state with combined unique data
        const value = Array.from(combinedDataMap.values());
        setState({ value, hash: v4() });
      });

      allUnsubscribers.push(unsubscribe);
    });

    const unSubscribe = () => {
      allUnsubscribers.forEach(unsub => unsub());
    };

    return { unSubscribe };
  },

  // Create a new distribution flow item or update an existing one
  save: async item => {
    try {
      const itemRef = doc(firestore, 'indexDistributionFlow', item.name);
      const itemDoc = await getDoc(itemRef);
      if (itemDoc.exists()) {
        // If it exists, update the existing document
        await updateDoc(itemRef, {
          ...item,
          status: item.status ?? 'pending',
          modified: new Date(),
        });
      } else {
        // If it doesn't exist, create a new document
        await setDoc(itemRef, {
          ...item,
          status: item.status ?? 'pending',
          modified: new Date(),
        });
      }
    } catch (error: any) {
      return { error: 'There was an error submitting request: ' + error.code };
    }
    return { ok: true };
  },
  delete: async name => {
    const itemRef = doc(firestore, 'indexDistributionFlow', name);
    await deleteDoc(itemRef);
    return { ok: true };
  },
});

export default createIndexDistributionFlowSlice;

const MAX_IN_CLAUSE = 10;

function chunkArray<T>(array: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
}
